import * as React from "react";
import styled from "@emotion/styled";
import placeholderAvatar from "assets/images/default-avatar.svg";
import {
  Box,
  Container,
  FlexRow,
  H1,
  IconCheckRounded,
  IconMapMarker,
  IconNewItem,
  IconPlusThick,
  Link,
  SocialIconLink,
  Stack,
  Text,
} from "atoms";
import type { User } from "features/auth";
import { FollowButton } from "features/follows";
import { GenericImage, ReviewableBanner } from "features/images";
import type { Artist } from "features/reviewable";
import { preventForwarding } from "styles/styleUtils";
import { paths } from "utils/paths";
import { ReviewLink, useUsersReview } from "features/reviews";
import { ArtistProfileEditMenu } from "./ArtistProfileEditMenu";
import { ArtistProfileShareMenu } from "./ArtistProfileShareMenu";

type ArtistProfileHeaderProps = {
  artist: Artist;
  user?: User;
  showAdminFeatures: boolean;
  isPoster: boolean;
};

type HeaderMenu = null | "share" | "edit" | "admin";

function ArtistProfileHeader({
  artist,
  user,
  showAdminFeatures,
  isPoster,
}: ArtistProfileHeaderProps) {
  const [menuOpen, setMenuOpen] = React.useState<HeaderMenu>(null);
  const createMenuToggle = (menu: HeaderMenu) => () => {
    setMenuOpen(menuOpen === menu ? null : menu);
  };
  const toggleShareMenu = React.useCallback(
    () => setMenuOpen((old) => (old === "share" ? null : "share")),
    [setMenuOpen],
  );

  const userIsCurrentArtist = !!user?.claimedArtistPageIds?.includes(artist.id);

  const { data: usersReview } = useUsersReview({
    entityId: artist.id,
    entityType: "ARTIST",
  });

  const reviewCountText = `${artist.reviewCount} Review${
    artist.reviewCount === 1 ? "" : "s"
  }`;
  const followerCountText = `${artist.followerCount ?? 0} Follower${
    (artist.followerCount ?? 0) === 1 ? "" : "s"
  }`;

  const locationTruncateLength = 30;
  const artistLocation = artist.country?.replace(" (the)", "");
  const locationIsTruncated =
    !!artistLocation?.length && artistLocation.length > locationTruncateLength;
  const artistLocationShort = artistLocation?.slice(0, locationTruncateLength);

  // truncated to reduce screen reader length
  const artistBio = artist.description?.slice(0, 170);

  const showArtistTools = userIsCurrentArtist || showAdminFeatures;

  return (
    <ArtistHeaderWrapper>
      <ArtistProfileBanner
        src={
          artist.bannerImage ??
          artist?.cardImage ??
          artist?.heroImage ??
          placeholderAvatar
        }
        alt=""
        aria-hidden="true"
        sizes="100vw"
        fallback={placeholderAvatar}
      />

      <Container width="lg">
        <ArtistHeaderBox>
          <ArtistPFP
            src={artist?.heroImage ?? placeholderAvatar}
            alt=""
            aria-hidden="true"
          />

          <ArtistDetails>
            <Stack gap="xxs">
              <ArtistName>
                <H1 size="xl" lang="en">
                  {artist.name ?? "Artist Name"}
                </H1>
                {artist.pronouns && <Pronouns>({artist.pronouns})</Pronouns>}
              </ArtistName>
              <Counts>
                <ArtistHeaderFlexRow>
                  {artistLocation && (
                    <>
                      <FlexRow
                        gap="1px"
                        itemsFlex="0 1 auto"
                        flexWrap="nowrap"
                        style={{ flex: "0 1 0%" }}
                      >
                        <IconWrapper aria-hidden>
                          <IconMapMarker primary="fg" />
                        </IconWrapper>
                        {locationIsTruncated ? (
                          <CountryText title={artistLocation}>
                            {artistLocationShort}...
                          </CountryText>
                        ) : (
                          <CountryText as="span">{artistLocation}</CountryText>
                        )}
                      </FlexRow>
                      <Pipe />
                    </>
                  )}

                  <FlexRow gap="10px" flexWrap="nowrap">
                    <CountsLink
                      to={paths.artistReviews(artist.id)}
                      aria-label={`${reviewCountText}. Jump to all reviews.`}
                    >
                      {reviewCountText}
                    </CountsLink>
                    <Pipe />

                    <CountsLink to={paths.artistFollowers(artist.id)}>
                      {followerCountText}
                    </CountsLink>
                  </FlexRow>
                </ArtistHeaderFlexRow>
              </Counts>
              {artistBio && (
                <ArtistHeaderFlexRow>
                  <ArtistBioWrapper>
                    <ArtistBio>{artistBio}</ArtistBio>
                    <ArtistBioLink
                      bioCharCount={artistBio.length}
                      to={paths.artistAbout_bio(artist.id)}
                    >
                      read full bio
                    </ArtistBioLink>
                  </ArtistBioWrapper>
                </ArtistHeaderFlexRow>
              )}
            </Stack>

            <ArtistLinks>
              <ArtistHeaderFlexRow>
                {artist.webSiteURL && (
                  <SocialIconLink
                    linkType="website"
                    href={artist.webSiteURL}
                    artistName={artist.name}
                    iconOnly
                  />
                )}
                {artist.instagramHandle && (
                  <SocialIconLink
                    linkType="instagram"
                    href={`https://instagram.com/${artist.instagramHandle}`}
                    artistName={artist.name}
                    iconOnly
                  />
                )}
                {artist.twitterHandle && (
                  <SocialIconLink
                    linkType="twitter"
                    href={`https://x.com/${artist.twitterHandle}`}
                    artistName={artist.name}
                    iconOnly
                  />
                )}
                {artist.youTubeHandle && (
                  <SocialIconLink
                    linkType="youtube"
                    href={`https://youtube.com/@${artist.youTubeHandle}`}
                    artistName={artist.name}
                    iconOnly
                  />
                )}
                {artist.tikTokHandle && (
                  <SocialIconLink
                    linkType="tiktok"
                    href={`https://tiktok.com/@${artist.tikTokHandle}`}
                    artistName={artist.name}
                    iconOnly
                  />
                )}
              </ArtistHeaderFlexRow>
            </ArtistLinks>

            <ArtistHeaderButtons>
              <ArtistHeaderFlexRow>
                {!userIsCurrentArtist && (
                  <SFollowButton
                    size="xxs"
                    entityId={artist.id}
                    followText={
                      <>
                        Follow <IconPlusThick size=".85em" />
                      </>
                    }
                    unfollowText={
                      <>
                        Following <IconCheckRounded />
                      </>
                    }
                  />
                )}

                {!(showArtistTools && !showAdminFeatures) && (
                  <SReviewLink
                    userIsSignedIn={!!user}
                    userIsCurrentArtist={userIsCurrentArtist}
                    userHasReviewed={!!usersReview}
                    artistId={artist.id}
                    size="xxs"
                  />
                )}

                {userIsCurrentArtist && isPoster && (
                  <Link
                    to={paths.artistUpdateCreate(artist.id)}
                    variant="primary"
                    size="xxs"
                  >
                    Post an Update <IconNewItem />
                  </Link>
                )}

                {showArtistTools && (
                  <ArtistProfileEditMenu
                    artistId={artist.id}
                    showAdminFeatures={showAdminFeatures}
                    isOpen={menuOpen === "edit"}
                    toggle={createMenuToggle("edit")}
                  />
                )}

                <ArtistProfileShareMenu
                  artist={artist}
                  isOpen={menuOpen === "share"}
                  toggle={toggleShareMenu}
                />
              </ArtistHeaderFlexRow>
            </ArtistHeaderButtons>
          </ArtistDetails>
        </ArtistHeaderBox>
      </Container>
    </ArtistHeaderWrapper>
  );
}

const ArtistHeaderWrapper = styled.div(({ theme }) => ({
  paddingBottom: theme.spacing.md,
}));

const ArtistProfileBanner = styled(ReviewableBanner)(({ theme }) => ({
  height: "calc(75px + 10vw)",
  maxHeight: 280,
  minHeight: 140,
  minWidth: "100%",
  overflow: "hidden",
  [theme.breakpoints.tablet]: {
    height: "calc(125px + 10vw)",
  },
  [theme.breakpoints.desktop]: {
    height: "auto",
    aspectRatio: "6",
  },
}));

const ArtistHeaderBox = styled(Box)(({ theme }) => ({
  marginTop: -50,
  overflow: "visible",
  position: "relative",
  display: "grid",
  gridTemplateAreas: `"pfp" "details"`,
  gap: ".66rem",
  justifyItems: "center",
  padding: theme.spacing.md,
  paddingTop: 0,
  [theme.breakpoints.tablet]: {
    marginTop: -75,
  },
  [theme.breakpoints.desktop]: {
    gridTemplateAreas: `
    "pfp details"`,
    gap: ".5rem 1rem",
    justifyContent: "flex-start",
    alignItems: "center",
    padding: theme.spacing.lg,
  },
  // screen width where the banner height maxes out at 320px
  "@media (min-width: 1280px)": {
    gap: ".5rem 1.5rem",
    marginTop: -100,
  },
}));

const ArtistHeaderFlexRow = styled.div(({ theme }) => ({
  display: "flex",
  gap: 10,
  justifyContent: "center",
  flexWrap: "wrap",
  alignItems: "center",
  maxWidth: `calc(100vw - (${theme.spacing.gutter} * 2))`,
  [theme.breakpoints.desktop]: {
    justifyContent: "flex-start",
  },
}));

const ArtistPFP = styled(GenericImage)(({ theme }) => ({
  gridArea: "pfp",
  width: 100,
  height: 100,
  // half of pfp height
  marginTop: -50,
  // reduce space between PFP and artist name
  marginBottom: "-.5rem",
  border: "3px solid",
  borderColor: theme.colors.bg,
  borderRadius: theme.borderRadius.round,
  overflow: "hidden",
  [theme.breakpoints.desktop]: {
    margin: 0,
    border: 0,
    width: 130,
    height: 130,
  },
  [theme.breakpoints.desktopMedium]: {
    width: 160,
    height: 160,
  },
}));

const ArtistName = styled.div(({ theme }) => ({
  gridArea: "name",
  paddingTop: 5,
  textAlign: "center",
  width: "100%",
  [theme.breakpoints.desktop]: {
    textAlign: "left",
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  h1: {
    margin: 0,
    lineHeight: 1,
  },
}));

const ArtistDetails = styled.div({
  gridArea: "details",
  width: "100%",
});

const ArtistLinks = styled.div({
  marginTop: 10,
  width: "100%",
});

const ArtistHeaderButtons = styled.div(({ theme }) => ({
  marginTop: 10,
  [theme.breakpoints.desktop]: {
    position: "absolute",
    marginTop: 0,
    right: 10,
    top: 10,
  },
  [theme.breakpoints.desktopMedium]: {
    right: "1rem",
    top: "1rem",
  },
}));

const Pronouns = styled(Text)(({ theme }) => ({
  color: theme.colors.fg70,
  fontSize: theme.fontSizes.xs,
  margin: 0,
  display: "block",
  [theme.breakpoints.desktop]: {
    display: "inline-block",
  },
}));

const Counts = styled.div(({ theme }) => ({
  color: theme.colors.fg,
  fontSize: 13,
  textAlign: "center",
  lineHeight: 1.2,
  whiteSpace: "nowrap",
}));

const CountsLink = styled(Link)({
  whiteSpace: "nowrap",
  flex: "0 1 0%",
});
const ArtistBioWrapper = styled(Text)(({ theme }) => ({
  lineHeight: 1.15,
  margin: 0,
  textAlign: "center",
  maxWidth: 620,
  position: "relative",
  fontSize: theme.fontSizes.xs,
  [theme.breakpoints.desktop]: {
    textAlign: "left",
  },
}));
const ArtistBio = styled.span({
  display: "-webkit-box",
  lineClamp: "2",
  overflow: "hidden",
  WebkitLineClamp: "2",
  WebkitBoxOrient: "vertical",
});
const ArtistBioLink = styled(Link, {
  shouldForwardProp: preventForwarding("bioCharCount"),
})<{ bioCharCount: number }>(({ bioCharCount, theme }) => ({
  backgroundColor: theme.colors.bg,
  color: "inherit",
  fontSize: theme.fontSizes.xxs,
  position: "absolute",
  right: 0,
  bottom: 0,
  display: bioCharCount < 64 ? "none" : "inline",
  [theme.breakpoints.xs]: {
    display: bioCharCount < 100 ? "none" : "inline",
  },
  [theme.breakpoints.tablet]: {
    display: bioCharCount < 140 ? "none" : "inline",
  },
  [theme.breakpoints.desktop]: {
    display: bioCharCount < 166 ? "none" : "inline",
  },

  "&:hover, &:focus-visible": {
    color: theme.colors.fg,
  },
  "&::after": {
    content: `"\\2026"`,
    position: "absolute",
    right: "calc(100% + 2px)",
    zIndex: 2,
  },
  "&::before": {
    content: "''",
    position: "absolute",
    right: "100%",
    top: -3,
    bottom: -3,
    width: "3em",
    zIndex: 1,
    backgroundImage: `
    linear-gradient(
      to right,
      rgba(0 0 0 / 0) 0%,
      rgba(255 255 255 / 1) 66%
    )`,
  },
}));

const IconWrapper = styled.div({
  display: "flex",
  alignitems: "center",
  justifyContent: "center",
  height: "1em",
  width: "1em",
  position: "relative",
  transform: "translateY(-2px)",
  zIndex: 2,
  svg: {
    height: "auto",
  },
});

const Pipe = styled.span({
  width: 1,
  height: ".8em",
  backgroundColor: "currentColor",
  opacity: 0.5,
  margin: "0 -2px",
});

const CountryText = styled(Text)({
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  overflow: "hidden",
  width: "auto",
  margin: 0,
});
const SFollowButton = styled(FollowButton)(({ theme }) => ({
  [theme.breakpoints.maxXs]: {
    paddingLeft: 10,
    paddingRight: 8,
  },
}));

const SReviewLink = styled(ReviewLink)(({ theme }) => ({
  [theme.breakpoints.maxXs]: {
    paddingLeft: 10,
    paddingRight: 8,
  },
}));
export { ArtistProfileHeader };
